import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

export default function ReplaceDocumentModal({ open, onClose, document, onSuccess }) {
  const [updating, setUpdating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isYouTubeVideo, setIsYouTubeVideo] = useState(false);

  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      url: '',
      title: '',
    },
    mode: 'onChange',
  });

  const title = watch('title');

  useEffect(() => {
    if (open && document) {
      // Determine if it's a video (YouTube or Wistia) — only title update allowed
      const isYouTube = document.url && (document.url.includes('youtube.com') || document.url.includes('youtu.be'));
      const isWistia = document.url && document.url.includes('wistia.com');
      setIsYouTubeVideo(isYouTube || isWistia);

      // Pre-populate form with existing document data
      reset({
        url: document.url || '',
        title: document.title || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document]);

  const handleClose = () => {
    if (!updating) {
      reset();
      setConfirmDialogOpen(false);
      onClose();
    }
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const onSubmit = async (data) => {
    setUpdating(true);

    try {
      // Update document metadata - only title and url are allowed
      const updateData = {
        title: data.title,
      };

      // For YouTube videos, only title can be updated
      // For other documents, both title and url can be updated
      if (!isYouTubeVideo) {
        updateData.url = data.url || '';
      }

      await api.admin.updateDocument(document.id, updateData);

      toast.success('Document updated successfully');
      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Failed to update document:', error);
      toast.error(error.message || 'Failed to update document');
    } finally {
      setUpdating(false);
      setConfirmDialogOpen(false);
    }
  };

  if (!document) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={updating}
      >
        <DialogTitle>
          <div className="flex justify-between items-center">
            <span>Update Document</span>
            <IconButton onClick={handleClose} disabled={updating} size="small">
              <X className="w-5 h-5" />
            </IconButton>
          </div>
        </DialogTitle>

        <form onSubmit={handleSubmit(handleOpenConfirmDialog)}>
          <DialogContent>
            <div className="space-y-4">
              {/* Title */}
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Title is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title *"
                    placeholder="Enter document title"
                    fullWidth
                    disabled={updating}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />

              {/* Document URL - only for non-YouTube documents */}
              {!isYouTubeVideo && (
                <Controller
                  name="url"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Document URL (optional)"
                      placeholder="Enter document URL"
                      fullWidth
                      disabled={updating}
                    />
                  )}
                />
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={updating}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!title || updating}
            >
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          Are you sure you want to update this document?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

